// ABOUTME: SSE streaming service for agent responses
// ABOUTME: Simple fetch-based streaming instead of WebSocket

import type { SDKStreamingMessage } from '@/types/sdk';

/**
 * Send a message and stream the response via SSE
 */
export async function* streamAgentResponse(
  investigationId: string,
  message: string,
  token: string,
  workspaceId?: string,
  clarificationAnswer?: boolean
): AsyncGenerator<SDKStreamingMessage> {
  const apiBaseUrl = import.meta.env.VITE_API_URL || '/api/v1';
  const url = `${apiBaseUrl}/investigations/${investigationId}/messages/stream`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  // Add workspace header if provided (skip 'ws_default' placeholder)
  if (workspaceId && workspaceId !== 'ws_default') {
    headers['X-Workspace-ID'] = workspaceId;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ content: message, clarification_answer: Boolean(clarificationAnswer) }),
  });

  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.statusText}`);
  }

  // Read the SSE stream
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (done) break;

    // Decode the chunk and add to buffer
    buffer += decoder.decode(value, { stream: true });

    // Process complete lines
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6).trim();

        if (!data) continue;

        try {
          const message = JSON.parse(data) as SDKStreamingMessage;
          yield message;

          // Stop if we receive completion message
          if (message.type === 'system' && message.subtype === 'complete') {
            return;
          }
        } catch (error) {
          console.error('Failed to parse SSE message:', error, data);
        }
      }
    }
  }
}
